import { useState, useEffect, useRef, useCallback } from "react";

const TIMEOUT_KEY  = "fb_session_timeout_mins";
const WARNING_SECS = 60; // show warning this many seconds before logout

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];

export function useSessionTimeout(onLogout: () => void) {
  const timeoutMins  = parseInt(localStorage.getItem(TIMEOUT_KEY) ?? "30", 10);
  const timeoutMs    = timeoutMins * 60 * 1000;

  const [showWarning, setShowWarning]   = useState(false);
  const [secondsLeft, setSecondsLeft]   = useState(WARNING_SECS);

  const lastActivityRef = useRef<number>(Date.now());
  const intervalRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const warningTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (showWarning) {
      setShowWarning(false);
      setSecondsLeft(WARNING_SECS);
      if (warningTimerRef.current) clearInterval(warningTimerRef.current);
    }
  }, [showWarning]);

  // Start warning countdown
  const startWarningCountdown = useCallback(() => {
    setShowWarning(true);
    setSecondsLeft(WARNING_SECS);
    if (warningTimerRef.current) clearInterval(warningTimerRef.current);
    warningTimerRef.current = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) {
          if (warningTimerRef.current) clearInterval(warningTimerRef.current);
          onLogout();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, [onLogout]);

  // Main inactivity check loop
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;
      const warningAt = timeoutMs - WARNING_SECS * 1000;
      if (idle >= timeoutMs) {
        onLogout();
      } else if (idle >= warningAt && !showWarning) {
        startWarningCountdown();
      }
    }, 5000);

    return () => {
      if (intervalRef.current)    clearInterval(intervalRef.current);
      if (warningTimerRef.current) clearInterval(warningTimerRef.current);
    };
  }, [timeoutMs, onLogout, showWarning, startWarningCountdown]);

  // Activity listeners
  useEffect(() => {
    const handler = () => { lastActivityRef.current = Date.now(); };
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, handler, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, handler));
  }, []);

  return { showWarning, secondsLeft, stayLoggedIn: resetActivity };
}

/** Call after fetching tenant info to persist the timeout for the session hook. */
export function setStoredTimeout(mins: number) {
  localStorage.setItem(TIMEOUT_KEY, String(mins));
}
